import { useCallback, useEffect, useState } from "react";
import html2canvas from 'html2canvas';
import { Box, Button, Modal, Typography, useCommonComponentContext } from "@maysoft/common-component-react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import StatisticsService from "services/medkit/statistics.service";
import Helpers from "commons/helpers";
import AssetService from "services/asset/asset.service";
import ReportService from "services/medkit/report.service";

interface IProps {
    id: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    input: string;
}

interface FishboneData {
    problem: string;
    categories: {
        [key: string]: string[];
    };
}

interface SolutionData {
    [key: string]: {
        details: string[];
        solutions: string[];
        title?: string;
        summary?: string;
        success?: boolean;
        error?: string;
    };
}

const reportService = new ReportService();
const assetService = new AssetService();
const statisticsService = new StatisticsService();

// Parse markdown response function
const parseMarkdownResponse = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const result = {
        title: '',
        details: [] as string[],
        solutions: [] as string[],
        summary: ''
    };

    let currentSection = '';
    let currentItem = '';

    lines.forEach(line => {
        const trimmedLine = line.trim();

        if (!trimmedLine) return;

        if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
            result.title = trimmedLine.replace(/\*\*/g, '');
            return;
        }

        if (/^\d+\.\s\*\*/.test(trimmedLine)) {
            if (currentItem) {
                result.solutions.push(currentItem.trim());
            }
            const match = trimmedLine.match(/^\d+\.\s\*\*(.*?)\*\*:?\s*(.*)/);
            if (match) {
                currentItem = `${ match[1].trim() }${ match[2] ? ': ' + match[2] : '' }`;
            }
            return;
        }

        if (trimmedLine.startsWith('-')) {
            const cleanItem = trimmedLine.substring(1).trim();
            if (currentItem) {
                currentItem += `\n   • ${ cleanItem }`;
            } else {
                result.solutions.push(cleanItem);
            }
            return;
        }

        if (trimmedLine.startsWith('**Tóm lại:**')) {
            if (currentItem) {
                result.solutions.push(currentItem.trim());
                currentItem = '';
            }
            result.summary = trimmedLine.replace('**Tóm lại:**', '').trim();
            return;
        }

        if (result.summary) {
            result.summary += ' ' + trimmedLine;
        } else if (currentItem) {
            currentItem += '\n' + trimmedLine;
        } else {
            result.details.push(trimmedLine);
        }
    });

    if (currentItem) {
        result.solutions.push(currentItem.trim());
    }

    return result;
};

const unescapeText = (input: string): string => {
    return input
        .replace(/\\r\\n/g, "\n")   // CRLF -> LF first, to avoid double newlines
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\n")
        .replace(/\\t/g, "\t")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\\\/g, "\\");   // unescape backslashes last
}

// Category icons and colors mapping với tên tiếng Việt
const categoryConfig = {
    "People": { color: "blue", name: "Con người" },
    "Process": { color: "green", name: "Quy trình" },
    "Technology": { color: "yellow", name: "Công nghệ" },
    "Materials": { color: "blue", name: "Vật liệu" },
    "Environment": { color: "green", name: "Môi trường" },
    "Management": { color: "yellow", name: "Quản lý" }
};

const FishboneModal = (props: IProps) => {
    const [visible, setVisible] = useState<boolean>(props.open);
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<FishboneData | null>(null);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [expandedCauses, setExpandedCauses] = useState<Set<string>>(new Set());
    const [solutionData, setSolutionData] = useState<SolutionData>({});
    const [loadingCauses, setLoadingCauses] = useState<Set<string>>(new Set());
    const { serviceCode, tenantCode } = useCommonComponentContext();

    // Edit states
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<string>('');
    const [editingCauseIndex, setEditingCauseIndex] = useState<number>(-1);
    const [editingCauseText, setEditingCauseText] = useState<string>('');
    const [isEditingDiagram, setIsEditingDiagram] = useState(false);

    // Add cause states
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addingCategory, setAddingCategory] = useState<string>('');
    const [newCauseText, setNewCauseText] = useState<string>('');

    // Edit root cause states
    const [editRootDialogOpen, setEditRootDialogOpen] = useState(false);
    const [editingRootCategory, setEditingRootCategory] = useState<string>('');
    const [editingRootCategoryName, setEditingRootCategoryName] = useState<string>('');

    const loadFishboneData = useCallback(async () => {
        const getFishBone = async (reason: string) => {
            try {
                const result: any = await statisticsService.getFishBone({ reason });

                if (result && result.length > 0 && result[0].text) {
                    const text = result[0].text.replace("```json", "").replace("```", "");
                    const parsedData = JSON.parse(text);
                    setData(parsedData);
                    setExpanded(Object.keys(parsedData.categories));
                }
            } catch (error) {
                Helpers.handleExceptionError(error);
            }
        };

        if (props.id) {
            try {
                setLoading(true);
                setData(null);
                const reportDetail = await reportService.detail(props.id);
                if (reportDetail.fishboneAnalysis) {
                    const fishboneAnalysis = JSON.parse(reportDetail.fishboneAnalysis);
                    setData(fishboneAnalysis);
                    setExpanded(Object.keys(fishboneAnalysis.categories));
                } else {
                    await getFishBone(props.input);
                }
                console.log("Report detail:", reportDetail);
            } catch (error) {
                Helpers.handleExceptionError(error);
                setData(null);
            } finally {
                setLoading(false);
            }
        }
    }, [props.id, props.input]);

    useEffect(() => {
        loadFishboneData();
    }, [loadFishboneData]);

    useEffect(() => {
        setVisible(props.open);
    }, [props.open]);

    const handleLinkReport = async () => {
        try {

            const element = document.getElementById('fishbone-diagram-export');
            if (!element) return;

            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true
            });

            canvas.toBlob(async (blob) => {
                if (blob) {

                    const file = new File(
                        [blob],
                        `sodoxuongca-${ Date.now() }.png`,
                        { type: 'image/png' }
                    );

                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("serviceCode", serviceCode);
                    formData.append("tenantCode", tenantCode);
                    const response = await assetService.uploadImage(formData);

                    const params = {
                        id: props.id,
                        fishboneAnalysis: JSON.stringify(data),
                        fishbonePhotoId: response.id
                    }
                    const result = await reportService.updateFishboneAnalysis(params);
                    console.log("Update result:", result);
                    Helpers.showAlert("Liên kết thành công!", "success");
                }
            });
        } catch (error) {
            Helpers.handleExceptionError(error);
        }
    }

    const handleAccordionChange = (category: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(prev =>
            isExpanded
                ? [...prev, category]
                : prev.filter(item => item !== category)
        );
    };

    const handleCauseClick = async (causeText: string, category: string) => {
        const causeKey = `${ category }-${ causeText }`;
        console.log("Clicked cause:", causeKey);

        if (expandedCauses.has(causeKey)) {
            const newExpanded = new Set(expandedCauses);
            newExpanded.delete(causeKey);
            setExpandedCauses(newExpanded);
            return;
        }

        if (!solutionData[causeKey]) {
            setLoadingCauses(prev => new Set([...prev, causeKey]));

            try {
                const detailResult: any = await statisticsService.getFishBoneSolution({
                    reason: props.input || "Bệnh nhân quậy phá không chịu uống thuốc",
                    fishboneFullContent: JSON.stringify(data),
                    fishboneChoosedContent: causeText
                });

                console.log("API params:", {
                    reason: props.input || "Bệnh nhân quậy phá không chịu uống thuốc",
                    fishboneFullContent: JSON.stringify(data),
                    fishboneChoosedContent: causeText
                });

                console.log("API response:", detailResult);

                if (detailResult && detailResult.length > 0 && detailResult[0].text) {
                    const textContent = detailResult[0].text;
                    const parsedContent = parseMarkdownResponse(unescapeText(textContent));
                    
                    setSolutionData(prev => ({
                        ...prev,
                        [causeKey]: {
                            details: parsedContent.details,
                            solutions: parsedContent.solutions,
                            title: parsedContent.title,
                            summary: parsedContent.summary,
                            success: true
                        }
                    }));
                } else {
                    setSolutionData(prev => ({
                        ...prev,
                        [causeKey]: {
                            details: [`Đang phân tích nguyên nhân: ${ causeText }`],
                            solutions: [],
                            success: false,
                            error: "Không có dữ liệu trả về từ API"
                        }
                    }));
                }

            } catch (error) {
                Helpers.handleExceptionError(error);
                setSolutionData(prev => ({
                    ...prev,
                    [causeKey]: {
                        details: [`Lỗi khi tải chi tiết cho: ${ causeText }`],
                        solutions: [],
                        success: false,
                        error: error instanceof Error ? error.message : "Lỗi không xác định"
                    }
                }));
            } finally {
                setLoadingCauses(prev => {
                    const newLoading = new Set(prev);
                    newLoading.delete(causeKey);
                    return newLoading;
                });
            }
        }

        setExpandedCauses(prev => new Set([...prev, causeKey]));
    };

    // Edit subcause handlers
    const handleEditClick = (category: string, causeIndex: number, causeText: string) => {
        setEditingCategory(category);
        setEditingCauseIndex(causeIndex);
        setEditingCauseText(causeText);
        setEditDialogOpen(true);
    };

    const handleSaveEdit = () => {
        if (!data || !editingCauseText.trim()) return;

        const newData = { ...data };
        newData.categories[editingCategory][editingCauseIndex] = editingCauseText.trim();
        setData(newData);

        setEditDialogOpen(false);
        setEditingCauseText('');
        setEditingCategory('');
        setEditingCauseIndex(-1);
    };

    const handleDeleteCause = (category: string, causeIndex: number) => {
        if (!data) return;

        const newData = { ...data };
        newData.categories[category].splice(causeIndex, 1);
        setData(newData);
    };

    // Add subcause handlers
    const handleAddCauseClick = (category: string) => {
        setAddingCategory(category);
        setNewCauseText('');
        setAddDialogOpen(true);
    };

    const handleSaveAddCause = () => {
        if (!data || !newCauseText.trim()) return;

        const newData = { ...data };
        newData.categories[addingCategory].push(newCauseText.trim());
        setData(newData);

        setAddDialogOpen(false);
        setNewCauseText('');
        setAddingCategory('');
    };

    // Edit root cause (category) handlers
    const handleEditRootClick = (category: string) => {
        const config = categoryConfig[category as keyof typeof categoryConfig];
        setEditingRootCategory(category);
        setEditingRootCategoryName(config ? config.name : category);
        setEditRootDialogOpen(true);
    };

    const handleSaveEditRoot = () => {
        if (!data || !editingRootCategoryName.trim()) return;

        const newData = { ...data };
        const causes = newData.categories[editingRootCategory];

        // Create new category with new name while maintaining order
        const newCategoryKey = editingRootCategoryName;

        // Rebuild categories object with same order, replacing old key with new key
        const newCategories: { [key: string]: string[] } = {};
        Object.keys(newData.categories).forEach(key => {
            if (key === editingRootCategory) {
                // Replace old key with new key at same position
                newCategories[newCategoryKey] = causes;
            } else {
                newCategories[key] = newData.categories[key];
            }
        });

        newData.categories = newCategories;
        setData(newData);

        // Update expanded state
        setExpanded(prev =>
            prev.map(cat => cat === editingRootCategory ? newCategoryKey : cat)
        );

        setEditRootDialogOpen(false);
        setEditingRootCategoryName('');
        setEditingRootCategory('');
    };

    const handleDeleteRootCause = (category: string) => {
        if (!data) return;

        const newData = { ...data };
        delete newData.categories[category];
        setData(newData);

        // Remove from expanded state
        setExpanded(prev => prev.filter(cat => cat !== category));
    };

    const getTotalCauses = () => {
        if (!data) return 0;
        return Object.values(data.categories).reduce((total, causes) => total + causes.length, 0);
    };

    const renderSolutionContent = (causeKey: string, config: any) => {
        const solution = solutionData[causeKey];
        if (!solution) return null;

        return (
            <Box sx={{
                ml: 4,
                mb: 1,
                p: 3,
                bgcolor: config.color === 'blue' ? '#5CB2FB08' : config.color === 'green' ? '#69E98208' : '#FDBE2208',
                borderRadius: 2,
                border: config.color === 'blue' ? '2px solid #5CB2FB30' : config.color === 'green' ? '2px solid #69E98230' : '2px solid #FDBE2230',
                boxShadow: 1
            }}>

                {solution.title && (
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: config.color === 'blue' ? '#5CB2FB' : config.color === 'green' ? '#69E982' : '#FDBE22',
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: '14px',
                            textAlign: 'center',
                            bgcolor: 'white',
                            p: 1.5,
                            borderRadius: 1,
                            border: config.color === 'blue' ? '1px solid #5CB2FB50' : config.color === 'green' ? '1px solid #69E98250' : '1px solid #FDBE2250'
                        }}
                    >
                        🎯 {solution.title}
                    </Typography>
                )}

                {solution.solutions && solution.solutions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#2e7d32',
                                fontWeight: 'bold',
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '13px'
                            }}
                        >
                            <LightbulbIcon sx={{ fontSize: 16 }} />
                            GIẢI PHÁP CHI TIẾT:
                        </Typography>

                        {solution.solutions.map((solutionItem, solutionIndex) => {
                            const lines = solutionItem.split('\n');
                            const mainLine = lines[0];
                            const subLines = lines.slice(1);

                            return (
                                <Box
                                    key={solutionIndex}
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        bgcolor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                        '&:hover': {
                                            boxShadow: 1,
                                            border: config.color === 'blue' ? '1px solid #5CB2FB50' : config.color === 'green' ? '1px solid #69E98250' : '1px solid #FDBE2250'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#2e7d32',
                                                fontWeight: 'bold',
                                                minWidth: '20px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {solutionIndex + 1}.
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#2e7d32',
                                                fontSize: '13px',
                                                lineHeight: 1.5,
                                                fontWeight: 600
                                            }}
                                        >
                                            {mainLine}
                                        </Typography>
                                    </Box>

                                    {subLines.length > 0 && (
                                        <Box sx={{ ml: 3 }}>
                                            {subLines.map((subLine, subIndex) => {
                                                const cleanSubLine = subLine.trim();
                                                if (!cleanSubLine) return null;

                                                return (
                                                    <Typography
                                                        key={subIndex}
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            color: '#555',
                                                            fontSize: '12px',
                                                            lineHeight: 1.6,
                                                            mb: 0.5,
                                                            pl: 1
                                                        }}
                                                    >
                                                        {cleanSubLine}
                                                    </Typography>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {solution.summary && (
                    <Box sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        borderRadius: 1,
                        border: '2px solid #28a745',
                        borderStyle: 'dashed'
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#28a745',
                                fontWeight: 'bold',
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '13px'
                            }}
                        >
                            📝 KẾT LUẬN:
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#495057',
                                fontSize: '12px',
                                lineHeight: 1.6,
                                fontStyle: 'italic'
                            }}
                        >
                            {solution.summary}
                        </Typography>
                    </Box>
                )}

                {(!solution.solutions || solution.solutions.length === 0) && !solution.summary && solution.success && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#ff9800',
                            fontSize: '12px',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            display: 'block'
                        }}
                    >
                        💭 Đang phân tích để đưa ra giải pháp phù hợp...
                    </Typography>
                )}
            </Box>
        );
    };

    const renderClassicFishbone = () => {
        if (!data) return null;

        const categories = Object.entries(data.categories);
        const totalCategories = categories.length;
        const halfPoint = Math.ceil(totalCategories / 2);

        const topCategories = categories.slice(0, halfPoint);
        const bottomCategories = categories.slice(halfPoint);

        const getColorByCategory = (category: string) => {
            const config = categoryConfig[category as keyof typeof categoryConfig];
            if (!config) return 'blue';
            return config.color;
        };

        const handleExportImage = async () => {
            const element = document.getElementById('fishbone-diagram-export');
            if (!element) return;

            try {
                const canvas = await html2canvas(element, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    logging: false,
                    useCORS: true
                });

                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `sodoxuongca-${ Date.now() }.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    }
                });
            } catch (error) {
                Helpers.handleExceptionError(error);
            }
        };

        return (
            <Box sx={{ mb: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Box></Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant={isEditingDiagram ? "contained" : "outlined"}
                            color={isEditingDiagram ? "secondary" : "primary"}
                            onClick={() => setIsEditingDiagram(!isEditingDiagram)}
                            startIcon={<EditIcon />}
                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                            {isEditingDiagram ? 'Hoàn tất chỉnh sửa' : 'Chỉnh sửa'}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleExportImage}
                            startIcon={<span>📥</span>}
                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Tải xuống ảnh
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleLinkReport}
                            startIcon={<span>🔗</span>}
                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Liên kết báo cáo
                        </Button>
                    </Box>
                </Box>

                {isEditingDiagram && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="caption">Chế độ chỉnh sửa: Hover vào các nguyên nhân hoặc danh mục trên sơ đồ để hiển thị nút chỉnh sửa</Typography>
                    </Alert>
                )}

                <Box
                    id="fishbone-diagram-export"
                    sx={{
                        bgcolor: 'white',
                        p: 4,
                        borderRadius: 1,
                        overflow: 'auto'
                    }}
                >
                    <style>{`
                    .fishbone-main-wrapper {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 2px;
                        position: relative;
                        font-family: 'Open Sans', sans-serif;
                    }
                    
                    .fishbone-tail-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                        z-index: 1;
                    }
                    
                    .fishbone-head-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                        z-index: 1;
                    }
                    
                    .fishbone-container {
                        display: inline-grid;
                        grid-template-columns: repeat(4, auto);
                        grid-template-rows: 1fr 4px 1fr;
                        align-items: center;
                    }
                    
                    .fishbone-container .cause {
                        display: flex;
                        flex-direction: column;
                        transform: skew(20deg);
                        transform-origin: bottom;
                        margin-left: 1.2em;
                        align-self: end;
                    }
                    
                    .fishbone-container .rootcause {
                        text-align: center;
                        position: relative;
                        left: 50%;
                        transform: translateX(-50%) skewX(-20deg);
                        font-size: 0.95em;
                        color: #fff;
                        padding: 0.5em 0.8em;
                        border-radius: 0.4em;
                        font-weight: bold;
                        margin-bottom: 0.3em;
                        max-width: 90%;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        word-break: break-word;
                        line-height: 1.3;
                        cursor: pointer;
                    }

                    .fishbone-container .rootcause-wrapper {
                        position: relative;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    }

                    .fishbone-container .rootcause-edit-btn {
                        opacity: 0;
                        transition: opacity 0.2s;
                        position: absolute;
                        right: -8px;
                        top: 50%;
                        transform: translateY(-50%) skewX(-20deg);
                        background: white;
                        border: 1px solid #1976d2;
                        border-radius: 4px;
                        width: 18px;
                        height: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        z-index: 10;
                    }

                    .fishbone-container .rootcause-wrapper:hover .rootcause-edit-btn {
                        opacity: 1;
                    }

                    .fishbone-container .rootcause-edit-btn:hover {
                        background-color: #e3f2fd;
                    }
                    
                    .fishbone-container .rootcause.yellow {
                        background-color: #FDBE22;
                    }
                    
                    .fishbone-container .rootcause.green {
                        background-color: #69E982;
                    }
                    
                    .fishbone-container .rootcause.blue {
                        background-color: #5CB2FB;
                    }
                    
                    .fishbone-container .subcause {
                        flex-grow: 1;
                        border-right: 0.2em solid #6B7280;
                        padding-bottom: 0;
                        padding-top: 1.2em;
                    }
                    
                    .fishbone-container .stat-wrapper {
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 4px;
                        margin-bottom: 0.9em;
                    }
                    
                    .fishbone-container .stat {
                        text-align: right;
                        padding-right: 3em;
                        position: relative;
                        transform: skewX(-20deg);
                        font-size: 0.85em;
                        color: #333;
                        max-width: 200px;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        word-break: break-word;
                        line-height: 1.5;
                        cursor: pointer;
                    }
                    
                    .fishbone-container .stat:before {
                        content: '';
                        display: block;
                        background-color: #6B7280;
                        position: absolute;
                        width: 3em;
                        height: 0.2em;
                        right: 0;
                        top: 50%;
                        transform: translate(0.2em, -50%);
                    }

                    .fishbone-container .stat-edit-btn {
                        opacity: 0;
                        transition: opacity 0.2s;
                        transform: skewX(-20deg);
                        position: absolute;
                        left: -25px;
                        background: white;
                        border: 1px solid #1976d2;
                        border-radius: 4px;
                        width: 20px;
                        height: 20px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        z-index: 10;
                    }
                    
                    .fishbone-container .stat-wrapper:hover .stat-edit-btn {
                        opacity: 1;
                    }

                    .fishbone-container .stat-edit-btn:hover {
                        background-color: #e3f2fd;
                    }
                    
                    .fishbone-container .line {
                        grid-column-start: 1;
                        grid-column-end: 4;
                        background-color: #6B7280;
                        height: 4px;
                        align-self: center;
                    }
                    
                    .fishbone-container .line ~ .cause {
                        transform: skewX(-20deg);
                        transform-origin: top;
                        align-self: start;
                    }
                    
                    .fishbone-container .line ~ .cause .rootcause {
                        transform: translateX(-50%) skewX(20deg);
                        margin-top: 0.3em;
                        margin-bottom: 0;
                        max-width: 90%;
                    }

                    .fishbone-container .line ~ .cause .rootcause-edit-btn {
                        transform: translateY(-50%) skewX(20deg);
                    }
                    
                    .fishbone-container .line ~ .cause .stat {
                        transform: skewX(20deg);
                    }

                    .fishbone-container .line ~ .cause .stat-edit-btn {
                        transform: skewX(20deg);
                    }
                    
                    .fishbone-container .line ~ .cause .subcause {
                        padding-bottom: 1.2em;
                        padding-top: 0;
                    }
                    
                    .fishbone-container .defect-spacer-top {
                        grid-column-start: 4;
                        grid-row-start: 1;
                        grid-row-end: 2;
                    }
                    
                    .fishbone-container .defect {
                        grid-column-start: 4;
                        grid-row-start: 2;
                        grid-row-end: 3;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    
                    .fishbone-container .defect-spacer-bottom {
                        grid-column-start: 4;
                        grid-row-start: 3;
                        grid-row-end: 4;
                    }
                `}</style>

                    <div className="fishbone-main-wrapper">
                        <div className="fishbone-tail-wrapper">
                            <img
                                src={require("../../assets/images/fish-tail.png")}
                                alt="Đuôi cá"
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                                crossOrigin="anonymous"
                            />
                        </div>

                        <div className="fishbone-container">
                            {topCategories.map(([category, causes]) => {
                                const config = categoryConfig[category as keyof typeof categoryConfig] ||
                                    { color: "blue", name: category };
                                const colorClass = getColorByCategory(category);

                                return (
                                    <div key={category} className="cause">
                                        <div className="rootcause-wrapper">
                                            <div className={`rootcause ${ colorClass }`} title={config.name}>
                                                {config.name}
                                            </div>
                                            {isEditingDiagram && (
                                                <div
                                                    className="rootcause-edit-btn"
                                                    onClick={() => handleEditRootClick(category)}
                                                >
                                                    <EditIcon sx={{ fontSize: 10, color: '#1976d2' }} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="subcause">
                                            {causes.map((cause, idx) => (
                                                <div key={idx} className="stat-wrapper">
                                                    {isEditingDiagram && (
                                                        <div
                                                            className="stat-edit-btn"
                                                            onClick={() => handleEditClick(category, idx, cause)}
                                                        >
                                                            <EditIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                                                        </div>
                                                    )}
                                                    <div className="stat" title={cause}>
                                                        {cause}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="line"></div>

                            {bottomCategories.map(([category, causes]) => {
                                const config = categoryConfig[category as keyof typeof categoryConfig] ||
                                    { color: "blue", name: category };
                                const colorClass = getColorByCategory(category);

                                return (
                                    <div key={category} className="cause">
                                        <div className="subcause">
                                            {causes.map((cause, idx) => (
                                                <div key={idx} className="stat-wrapper">
                                                    {isEditingDiagram && (
                                                        <div
                                                            className="stat-edit-btn"
                                                            onClick={() => handleEditClick(category, idx, cause)}
                                                        >
                                                            <EditIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                                                        </div>
                                                    )}
                                                    <div className="stat" title={cause}>
                                                        {cause}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="rootcause-wrapper">
                                            <div className={`rootcause ${ colorClass }`} title={config.name}>
                                                {config.name}
                                            </div>
                                            {isEditingDiagram && (
                                                <div
                                                    className="rootcause-edit-btn"
                                                    onClick={() => handleEditRootClick(category)}
                                                >
                                                    <EditIcon sx={{ fontSize: 10, color: '#1976d2' }} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="defect-spacer-top"></div>
                            <div className="defect"></div>
                            <div className="defect-spacer-bottom"></div>
                        </div>

                        <div className="fishbone-head-wrapper">
                            <img
                                src={require("../../assets/images/fish-head.png")}
                                alt="Đầu cá"
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                                crossOrigin="anonymous"
                            />
                        </div>
                    </div>
                </Box>

                <Box sx={{
                    mt: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                }}>
                    {Object.keys(data.categories).map((category) => {
                        const config = categoryConfig[category as keyof typeof categoryConfig] ||
                            { color: "blue", name: category };
                        const colorHex = config.color === 'blue' ? '#5CB2FB' :
                            config.color === 'green' ? '#69E982' : '#FDBE22';

                        return (
                            <Chip
                                key={category}
                                label={config.name}
                                sx={{
                                    bgcolor: colorHex + '20',
                                    color: colorHex,
                                    fontWeight: 'bold',
                                    border: `2px solid ${ colorHex }`,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: colorHex + '40',
                                        transform: 'scale(1.05)',
                                        transition: 'all 0.2s',
                                        boxShadow: 2
                                    }
                                }}
                                onClick={() => {
                                    const element = document.getElementById(`category-${ category }`);
                                    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            />
                        );
                    })}
                </Box>

                <Box sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: '#e3f2fd',
                    borderRadius: 1,
                    border: '1px solid #2196f3'
                }}>
                    <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 'bold', mb: 1 }}>
                        💡 Hướng dẫn sử dụng:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                        • Hover chuột vào text để xem nội dung đầy đủ
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                        • Click nút "Chỉnh sửa" để bật chế độ chỉnh sửa sơ đồ và danh mục
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                        • Click nút "Tải xuống ảnh" để export sơ đồ thành file PNG
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                        • Click vào chip màu để xem chi tiết phân tích bên dưới
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Modal
            fullWidth
            allowOutsideClick={!loading}
            hasActionButton={!loading}
            // hasActionButton
            maxWidth={"xl"}
            visible={visible}
            title="Sơ đồ xương cá"
            closeButton="Đóng"
            onClose={() => {
                setVisible(false);
                props.setOpen(false);
            }}
        >
            <Box className="mb-3" flexDirection="column" display="flex" gap={3}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                        <Typography variant="body1" sx={{ ml: 2 }}>
                            Đang phân tích nguyên nhân...
                        </Typography>
                    </Box>
                ) : data ? (
                    <>
                        {renderClassicFishbone()}

                        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
                            <Chip
                                label={`${ Object.keys(data.categories).length } danh mục`}
                                color="primary"
                                variant="outlined"
                            />
                            <Chip
                                label={`${ getTotalCauses() } nguyên nhân`}
                                color="secondary"
                                variant="outlined"
                            />
                            <Chip
                                label="Nhấp vào nguyên nhân để xem giải pháp chi tiết"
                                color="info"
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: "center" }}>
                                📋 PHÂN TÍCH NGUYÊN NHÂN THEO DANH MỤC
                            </Typography>

                            {Object.entries(data.categories).map(([category, causes], index) => {
                                const config = categoryConfig[category as keyof typeof categoryConfig] ||
                                    { color: "blue", name: category };

                                const colorHex = config.color === 'blue' ? '#5CB2FB' : config.color === 'green' ? '#69E982' : '#FDBE22';

                                return (
                                    <Accordion
                                        key={category}
                                        id={`category-${ category }`}
                                        expanded={expanded.includes(category)}
                                        onChange={handleAccordionChange(category)}
                                        sx={{
                                            mb: 2,
                                            boxShadow: 2,
                                            '&:before': {
                                                display: 'none',
                                            },
                                            borderRadius: '8px !important',
                                            scrollMarginTop: '20px'
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                bgcolor: colorHex + '20',
                                                borderRadius: expanded.includes(category) ? '8px 8px 0 0' : '8px',
                                                '& .MuiAccordionSummary-content': {
                                                    alignItems: 'center'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: colorHex,
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {config.name}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={`${ causes.length } nguyên nhân`}
                                                        sx={{
                                                            backgroundColor: colorHex + '20',
                                                            color: colorHex,
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                </Box>
                                                <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditRootClick(category)}
                                                        sx={{
                                                            color: colorHex,
                                                            '&:hover': { bgcolor: colorHex + '20' }
                                                        }}
                                                    >
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            Helpers.showConfirmAlert(
                                                                'Bạn có chắc muốn xóa toàn bộ danh mục này?',
                                                                async () => { handleDeleteRootCause(category) },
                                                            );
                                                        }}
                                                        sx={{
                                                            color: '#d32f2f',
                                                            '&:hover': { bgcolor: '#ffebee' }
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0 }}>
                                            <List dense>
                                                {causes.map((cause, causeIndex) => {
                                                    const causeKey = `${ category }-${ cause }`;
                                                    const isExpanded = expandedCauses.has(causeKey);
                                                    const isLoading = loadingCauses.has(causeKey);
                                                    const hasSolution = solutionData[causeKey];

                                                    return (
                                                        <Box key={causeIndex}>
                                                            <ListItem
                                                                sx={{
                                                                    py: 1,
                                                                    '&:hover': {
                                                                        bgcolor: colorHex + '10',
                                                                        borderRadius: 1
                                                                    },
                                                                    borderRadius: 1,
                                                                    transition: 'background-color 0.2s'
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 24 }}>
                                                                    <FiberManualRecordIcon
                                                                        sx={{
                                                                            fontSize: 8,
                                                                            color: colorHex
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={cause}
                                                                    primaryTypographyProps={{
                                                                        variant: 'body2',
                                                                        sx: { lineHeight: 1.6 }
                                                                    }}
                                                                />
                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleEditClick(category, causeIndex, cause)}
                                                                        sx={{
                                                                            color: colorHex,
                                                                            '&:hover': { bgcolor: colorHex + '20' }
                                                                        }}
                                                                    >
                                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>

                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            Helpers.showConfirmAlert(
                                                                                'Bạn có chắc muốn xóa nguyên nhân này?',
                                                                                async () => { handleDeleteCause(category, causeIndex) },
                                                                            );
                                                                        }}
                                                                        sx={{
                                                                            color: '#d32f2f',
                                                                            '&:hover': { bgcolor: '#ffebee' }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>

                                                                    {isLoading ? (
                                                                        <CircularProgress size={16} />
                                                                    ) : (
                                                                        <Button
                                                                            size="small"
                                                                            variant={isExpanded ? "contained" : "outlined"}
                                                                            onClick={() => handleCauseClick(cause, category)}
                                                                            sx={{
                                                                                minWidth: 'auto',
                                                                                px: 1.5,
                                                                                py: 0.5,
                                                                                fontSize: '11px',
                                                                                backgroundColor: isExpanded ? colorHex : 'transparent',
                                                                                borderColor: colorHex,
                                                                                color: isExpanded ? 'white' : colorHex,
                                                                                '&:hover': {
                                                                                    backgroundColor: isExpanded ? colorHex : colorHex + '20',
                                                                                    borderColor: colorHex,
                                                                                }
                                                                            }}
                                                                            startIcon={
                                                                                hasSolution ?
                                                                                    <CheckCircleIcon sx={{ fontSize: 12 }} /> :
                                                                                    <LightbulbIcon sx={{ fontSize: 12 }} />
                                                                            }
                                                                        >
                                                                            {isExpanded ? 'Thu gọn' : (hasSolution ? 'Xem giải pháp' : 'Phân tích')}
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            </ListItem>

                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                {renderSolutionContent(causeKey, config)}
                                                            </Collapse>
                                                        </Box>
                                                    );
                                                })}
                                            </List>

                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddCauseClick(category)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        color: colorHex,
                                                        borderColor: colorHex,
                                                        '&:hover': {
                                                            bgcolor: colorHex + '20',
                                                            borderColor: colorHex
                                                        }
                                                    }}
                                                    variant="outlined"
                                                >
                                                    Thêm nguyên nhân
                                                </Button>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>

                        <Box sx={{
                            p: 2,
                            bgcolor: "#e8f4fd",
                            borderRadius: 2,
                            border: "1px solid #1976d2"
                        }}>
                            <Typography variant="body2" sx={{ color: "#1976d2", fontStyle: 'italic' }}>
                                💡 <strong>Hướng dẫn:</strong>
                                <br />• Nhấp vào các danh mục trên sơ đồ xương cá để cuộn xuống phần chi tiết
                                <br />• Nhấp vào các nguyên nhân trên sơ đồ hoặc nút "Phân tích" để xem giải pháp đề xuất
                                <br />• Sử dụng các nút Chỉnh sửa/Xóa/Thêm để điều chỉnh danh mục và nguyên nhân
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <Box textAlign="center" mt={4}>
                        <Typography variant="body1" color="error">
                            Không thể tải dữ liệu phân tích. Vui lòng thử lại.
                        </Typography>
                        <Button
                            sx={{ mt: 1 }}
                            variant="contained"
                            onClick={loadFishboneData}
                        >
                            Thử lại
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Dialog chỉnh sửa subcause */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Chỉnh sửa nguyên nhân
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nội dung nguyên nhân"
                        fullWidth
                        multiline
                        rows={3}
                        value={editingCauseText}
                        onChange={(e) => setEditingCauseText(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        Hủy
                    </Button>
                    <Button
                        onClick={handleSaveEdit}
                        variant="contained"
                        color="primary"
                        disabled={!editingCauseText.trim()}
                    >
                        Lưu
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog thêm nguyên nhân mới */}
            <Dialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Thêm nguyên nhân mới
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nội dung nguyên nhân"
                        placeholder="Nhập nguyên nhân mới..."
                        fullWidth
                        multiline
                        rows={3}
                        value={newCauseText}
                        onChange={(e) => setNewCauseText(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>
                        Hủy
                    </Button>
                    <Button
                        onClick={handleSaveAddCause}
                        variant="contained"
                        color="primary"
                        disabled={!newCauseText.trim()}
                    >
                        Thêm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog chỉnh sửa root cause (category) */}
            <Dialog
                open={editRootDialogOpen}
                onClose={() => setEditRootDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Chỉnh sửa danh mục
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Tên danh mục"
                        fullWidth
                        value={editingRootCategoryName}
                        onChange={(e) => setEditingRootCategoryName(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditRootDialogOpen(false)}>
                        Hủy
                    </Button>
                    <Button
                        onClick={handleSaveEditRoot}
                        variant="contained"
                        color="primary"
                        disabled={!editingRootCategoryName.trim()}
                    >
                        Lưu
                    </Button>
                </DialogActions>
            </Dialog>
        </Modal>
    );
};

export default FishboneModal;